import { ChangeEvent, memo, useMemo, useState, useEffect } from 'react';
import { Location } from 'history';
import { useTranslation } from 'react-i18next';
import { FormikErrors } from 'formik';
import _get from 'lodash/get';
import Autocomplete from '@mui/material/Autocomplete';

import type { UserSettings } from 'models';

import { toDate, toUpperFirstChar } from 'utils';

import { FileLoader, EntityType } from 'features/file-loader';

import {
  PageLoader,
  Error,
  BorderedBox,
  PageTitle,
  Flex,
  TitledBox,
  ValidationWarning,
  Checkbox,
  Radio,
  Loader,
  RouteLeavingGuard,
  DatePicker,
  SaveConfirm,
} from 'components';
import {
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  RadioGroup,
  FormLabel,
  Tooltip,
} from '@material-ui/core';
import { ButtonShape } from 'components/styled-components';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoIcon from '@material-ui/icons/InfoOutlined';

import { ThemeProvider, theme } from 'configs/theme-configurator';

import clsx from 'clsx';
import { useStyles } from './style';

type ModifiedUserSettings = Omit<UserSettings, 'HasSocialSecurityNumber'> & {
  SocialSecurityNumber: string | null;
};

interface Props {
  values: ModifiedUserSettings | null;
  errors: FormikErrors<ModifiedUserSettings>;
  countries: string[];
  loading: boolean;
  error: boolean;
  upload: boolean;
  openAlternative: boolean;
  useAlternativeEmail: boolean;
  useMailForMeeting: boolean;
  useMailForRealise: boolean;
  hasSSN: boolean;
  useAlternativeAddress: boolean;
  queue: Function[];
  saved: boolean;
  equal: boolean;
  navigate: (path: string) => void;
  shouldBlockNavigation: (location: Location) => boolean;
  handleToggleCustomCheckbox: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangeInput: (e: ChangeEvent<HTMLInputElement>) => void;
  handleToggleAlternativeView: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangeCountry: (e: ChangeEvent<unknown>, contry: string | null) => void;
  handleChangeAlternativeCountry: (
    e: ChangeEvent<unknown>,
    contry: string | null
  ) => void;
  handleToggleCheckbox: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangeTax: (e: ChangeEvent<HTMLInputElement>) => void;
  addBankAccount: () => void;
  removeBankAccount: (id: string) => void;
  handleChangeRadio: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSubmitForm: () => Promise<void>;
  handleInitErrors: () => void;
  handleChangeDate: (date: Date | null, name?: string) => void;
  clearQueue(): void;
}

const currentYear = new Date().getFullYear();

const PersonInformationDetails: React.FC<Props> = ({
  values,
  loading,
  error,
  countries,
  openAlternative,
  equal,
  useAlternativeAddress,
  useAlternativeEmail,
  errors,
  upload,
  handleChangeInput,
  handleChangeCountry,
  handleChangeTax,
  handleToggleAlternativeView,
  handleToggleCustomCheckbox,
  handleToggleCheckbox,
  addBankAccount,
  removeBankAccount,
  handleChangeRadio,
  handleSubmitForm,
  handleInitErrors,
  navigate,
  shouldBlockNavigation,
  handleChangeDate,
  hasSSN,
  queue,
  saved,
  handleChangeAlternativeCountry,
  useMailForMeeting,
  useMailForRealise,
  clearQueue,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [openSSN, setOpenSSN] = useState(false);
  const [openSaveModal, setOpenSaveModal] = useState(false);

  useEffect(() => {
    if (!saved && queue.length) {
      setOpenSaveModal(true);
    }
  }, [queue, saved]);

  const handleOpenSSN = () => {
    setOpenSSN(true);
  };

  const handleCloseSSN = () => {
    setOpenSSN(false);
  };

  const onSubmit = async () => {
    setOpenSSN(false);
    await handleSubmitForm();
  };

  const inputProps = useMemo(
    () => ({
      disableUnderline: true,
      classes: {
        input: classes.input,
      },
    }),
    [classes]
  );
  const inputClasses = useMemo(
    () => ({
      root: classes.inputBorder,
    }),
    [classes]
  );
  const formControlLabelClasses = useMemo(
    () => ({
      label: classes.formControlLabel,
      root: classes.formControlLabelRoot,
    }),
    [classes]
  );
  const formLabelClasses = useMemo(
    () => ({
      root: classes.formLabel,
    }),
    [classes]
  );
  const datePickerClasses = useMemo(
    () => ({
      output: {
        root: clsx(classes.datePicker, {
          [classes.datePickerError]: !values?.Birthday,
        }),
        text: classes.datePickerText,
      },
    }),
    [classes, values?.Birthday]
  );
  const socialSecurityInput = useMemo(
    () => ({
      disableUnderline: true,
    }),
    [classes]
  );

  if (loading) {
    return <PageLoader />;
  }

  if (error || !values) {
    return <Error />;
  }

  const disabledFields = upload || Boolean(!values.CanEditUserSettings);

  const renderAlternativeEmail = () => {
    return (
      <div className={classes.pl35}>
        <div className={classes.checkboxColumn}>
          <Checkbox
            name="useAlternativeEmail"
            checked={useAlternativeEmail}
            onChange={handleToggleCustomCheckbox}
            disabled={disabledFields}
          />
          <span className={classes.checkboxLabel}>
            {t('#portal.usersettings.label.email')}
          </span>
        </div>
        {useAlternativeEmail ? (
          <div className={clsx(classes.directionColumn, classes.mt20)}>
            <span className={classes.label}>
              Sähköpostiosoite
              <sup>*</sup>
            </span>
            <ValidationWarning
              text={_get(errors, 'AlternativeMailEmailEmail')}
              handleInitErrors={handleInitErrors}
              Component={
                <TextField
                  value={values.AlternativeMailEmail?.Email || ''}
                  name="AlternativeMailEmail.Email"
                  placeholder="mail@mail.com"
                  onChange={handleChangeInput}
                  classes={inputClasses}
                  InputProps={inputProps}
                  disabled={disabledFields}
                />
              }
            />
          </div>
        ) : null}
      </div>
    );
  };

  const renderAlternativeAddress = () => {
    return (
      <div className={classes.pl35}>
        <div className={classes.checkboxColumn}>
          <Checkbox
            name="useAlternativeAddress"
            checked={useAlternativeAddress}
            onChange={handleToggleCustomCheckbox}
            disabled={disabledFields}
          />
          <span className={classes.checkboxLabel}>
            {t('#portal.usersettings.label.post')}
          </span>
        </div>
        {useAlternativeAddress ? (
          <div className={classes.mt20}>
            <div className={clsx(classes.directionColumn, classes.mb20)}>
              <span className={classes.label}>
                {t('#portal.usersettings.label.address')}
                <sup>*</sup>
              </span>
              <ValidationWarning
                text={_get(errors, 'AlternativeMailAddressStreet1')}
                handleInitErrors={handleInitErrors}
                Component={
                  <TextField
                    value={values.AlternativeMailAddress?.Street1 || ''}
                    name="AlternativeMailAddress.Street1"
                    onChange={handleChangeInput}
                    classes={inputClasses}
                    InputProps={inputProps}
                    disabled={disabledFields}
                  />
                }
              />
            </div>
            {values.AlternativeMailAddress?.Street1 ||
            values.AlternativeMailAddress?.Street2 ? (
              <div className={clsx(classes.directionColumn, classes.mb20)}>
                <TextField
                  value={values.AlternativeMailAddress?.Street2 || ''}
                  name="AlternativeMailAddress.Street2"
                  onChange={handleChangeInput}
                  classes={inputClasses}
                  InputProps={inputProps}
                  disabled={disabledFields}
                />
              </div>
            ) : null}
            {values.AlternativeMailAddress?.Street2 ||
            values.AlternativeMailAddress?.Street3 ? (
              <div className={clsx(classes.directionColumn, classes.mb20)}>
                <TextField
                  value={values.AlternativeMailAddress?.Street3 || ''}
                  name="AlternativeMailAddress.Street3"
                  onChange={handleChangeInput}
                  classes={inputClasses}
                  InputProps={inputProps}
                  disabled={disabledFields}
                />
              </div>
            ) : null}
            <div className={clsx(classes.flex, classes.mb20)}>
              <div className={classes.directionColumn}>
                <span className={classes.label}>
                  {t('#portal.usersettings.label.zipcode')}
                  <sup>*</sup>
                </span>
                <ValidationWarning
                  text={_get(errors, 'AlternativeMailAddressPostalCode')}
                  handleInitErrors={handleInitErrors}
                  Component={
                    <TextField
                      value={values.AlternativeMailAddress?.PostalCode || ''}
                      name="AlternativeMailAddress.PostalCode"
                      onChange={handleChangeInput}
                      classes={inputClasses}
                      InputProps={inputProps}
                      disabled={disabledFields}
                    />
                  }
                />
              </div>
              <div className={clsx(classes.directionColumn, classes.mb20)}>
                <span className={classes.label}>
                  {t('#portal.usersettings.label.postalarea')}
                  <sup>*</sup>
                </span>
                <ValidationWarning
                  text={_get(errors, 'AlternativeMailAddressPostalArea')}
                  handleInitErrors={handleInitErrors}
                  Component={
                    <TextField
                      value={values.AlternativeMailAddress?.PostalArea || ''}
                      name="AlternativeMailAddress.PostalArea"
                      onChange={handleChangeInput}
                      classes={inputClasses}
                      InputProps={inputProps}
                      disabled={disabledFields}
                    />
                  }
                />
              </div>
            </div>
            <div className={clsx(classes.directionColumn, classes.mb20)}>
              <span className={classes.label}>
                {t('#portal.usersettings.label.country')}
                <sup>*</sup>
              </span>
              <ValidationWarning
                text={_get(errors, 'AlternativeMailAddressCountry')}
                handleInitErrors={handleInitErrors}
                Component={
                  <Autocomplete
                    disabled={disabledFields}
                    value={values.AlternativeMailAddress?.Country || ''}
                    options={countries}
                    onChange={handleChangeAlternativeCountry}
                    classes={{
                      paper: classes.paper,
                    }}
                    renderInput={(params) => (
                      <TextField
                        name="AlternativeMailAddress.Country"
                        classes={{
                          root: classes.inputBorder,
                        }}
                        InputProps={{
                          ref: params.InputProps.ref,
                          disableUnderline: true,
                          classes: {
                            input: classes.input,
                          },
                          inputProps: { ...params.inputProps },
                        }}
                      />
                    )}
                  />
                }
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  };

  const renderAlternative = () => {
    return (
      <>
        {renderAlternativeEmail()}
        {renderAlternativeAddress()}
      </>
    );
  };

  const handleCloseSaveModal = () => {
    setOpenSaveModal(false);
    queue.forEach((fn) => fn());
    clearQueue();
  };

  const handleApply = async () => {
    setOpenSaveModal(false);
    await onSubmit();
    queue.forEach((fn) => fn());
    clearQueue();
  };

  return (
    <div>
      <SaveConfirm
        open={openSaveModal}
        apply={handleApply}
        cancel={handleCloseSaveModal}
      />
      <RouteLeavingGuard
        apply={onSubmit}
        navigate={navigate}
        when={!equal}
        shouldBlockNavigation={shouldBlockNavigation}
      />
      <div className={classes.topBar}>
        <PageTitle className={classes.title}>
          {toUpperFirstChar(t('#portal.usersettings.menu.label'))}
        </PageTitle>
        {values.CanEditUserSettings ? (
          <ButtonShape
            onClick={onSubmit}
            className={classes.saveBtn}
            disabled={upload}
          >
            {t('#portal.common.button.save')}
            {upload ? (
              <div className={classes.ml10}>
                <Loader size={17} cls={{ root: classes.loaderRoot }} />
              </div>
            ) : null}
          </ButtonShape>
        ) : null}
      </div>
      <form>
        <BorderedBox>
          <Flex>
            <TitledBox
              title={'G_Basic'}
              cls={{
                box: clsx(classes.titledBox, classes.p15),
              }}
            >
              <div className={classes.flex}>
                <div className={clsx(classes.box, classes.directionColumn)}>
                  <span className={classes.label}>
                    {t('##attribute#contact#firstname')}
                  </span>
                  <span className={classes.text}>{values.FirstName}</span>
                </div>
                <div className={clsx(classes.box, classes.directionColumn)}>
                  <span className={classes.label}>
                    {t('##attribute#contact#lastname')}
                  </span>
                  <span className={classes.text}>{values.LastName}</span>
                </div>
              </div>
              <div className={classes.directionColumn}>
                <div className={clsx(classes.flex, classes.mb5)}>
                  <span className={clsx(classes.label, classes.mb0)}>
                    {t('##attribute#contact#uds_socialsecuritynumber')}
                  </span>
                  <Tooltip
                    classes={{ tooltip: classes.tooltip }}
                    title={
                      'Henkilötunnus on piilotettu näkyvistä tallentamisen jälkeen'
                    }
                  >
                    <InfoIcon className={classes.tooltipIcon} />
                  </Tooltip>
                </div>
                <ValidationWarning
                  text={errors.SocialSecurityNumber}
                  handleInitErrors={handleInitErrors}
                  Component={
                    <TextField
                      disabled={disabledFields}
                      value={values.SocialSecurityNumber}
                      name="SocialSecurityNumber"
                      placeholder={
                        hasSSN
                          ? !openSSN
                            ? 'Henkilötunnus annettu, päivitä henkilötunnus halutessasi'
                            : 'DDMMYY(A/-)NNNI'
                          : 'DDMMYY(A/-)NNNI'
                      }
                      onChange={handleChangeInput}
                      onFocus={handleOpenSSN}
                      onBlur={handleCloseSSN}
                      classes={inputClasses}
                      InputProps={socialSecurityInput}
                    />
                  }
                />
              </div>
              <div className={classes.directionColumn}>
                <span className={classes.label}>
                  Syntymäaika <sup>*</sup>
                </span>
                <ValidationWarning
                  text={errors.Birthday}
                  handleInitErrors={handleInitErrors}
                  Component={
                    <DatePicker
                      date={toDate(values.Birthday)}
                      handleChange={handleChangeDate}
                      name="Birthday"
                      endIn={currentYear}
                      placeholder="D.M.YYYY"
                      cls={datePickerClasses}
                      disabled={disabledFields}
                    />
                  }
                />
              </div>
              <div className={classes.directionColumn}>
                <span className={classes.label}>
                  {t('##attribute#contact#emailaddress1')} <sup>*</sup>
                </span>
                <ValidationWarning
                  text={_get(errors, 'EmailEmail')}
                  handleInitErrors={handleInitErrors}
                  Component={
                    <TextField
                      disabled={disabledFields}
                      value={values.Email?.Email || ''}
                      onChange={handleChangeInput}
                      name="Email.Email"
                      placeholder="mail@mail.com"
                      classes={inputClasses}
                      InputProps={inputProps}
                    />
                  }
                />
              </div>
              <div className={classes.directionColumn}>
                <span className={classes.label}>
                  {t('##attribute#contact#mobilephone')} <sup>*</sup>
                </span>
                <ValidationWarning
                  text={_get(errors, 'PhonePhone')}
                  handleInitErrors={handleInitErrors}
                  Component={
                    <TextField
                      disabled={disabledFields}
                      value={values.Phone?.Phone || ''}
                      onChange={handleChangeInput}
                      name="Phone.Phone"
                      placeholder="+1234567890"
                      classes={inputClasses}
                      InputProps={inputProps}
                    />
                  }
                />
              </div>
              <div className={classes.directionColumn}>
                <span className={classes.label}>
                  {t('#portal.usersettings.label.street')} <sup>*</sup>
                </span>
                <ValidationWarning
                  text={_get(errors, 'AddressStreet1')}
                  handleInitErrors={handleInitErrors}
                  Component={
                    <TextField
                      disabled={disabledFields}
                      value={values.Address?.Street1 || ''}
                      onChange={handleChangeInput}
                      name="Address.Street1"
                      classes={inputClasses}
                      InputProps={inputProps}
                    />
                  }
                />
              </div>
              {values.Address?.Street1 || values.Address?.Street2 ? (
                <div className={classes.directionColumn}>
                  <TextField
                    disabled={disabledFields}
                    value={values.Address?.Street2 || ''}
                    onChange={handleChangeInput}
                    name="Address.Street2"
                    classes={inputClasses}
                    InputProps={inputProps}
                  />
                </div>
              ) : null}
              {values.Address?.Street2 || values.Address?.Street3 ? (
                <div className={classes.directionColumn}>
                  <TextField
                    disabled={disabledFields}
                    value={values.Address?.Street3 || ''}
                    onChange={handleChangeInput}
                    name="Address.Street3"
                    classes={inputClasses}
                    InputProps={inputProps}
                  />
                </div>
              ) : null}
              <div className={classes.flex}>
                <div className={classes.directionColumn}>
                  <span className={classes.label}>
                    {t('#portal.usersettings.label.postalcode')} <sup>*</sup>
                  </span>
                  <ValidationWarning
                    text={_get(errors, 'AddressPostalCode')}
                    handleInitErrors={handleInitErrors}
                    Component={
                      <TextField
                        disabled={disabledFields}
                        value={values.Address?.PostalCode || ''}
                        onChange={handleChangeInput}
                        name="Address.PostalCode"
                        classes={inputClasses}
                        InputProps={inputProps}
                      />
                    }
                  />
                </div>
                <div className={classes.directionColumn}>
                  <span className={classes.label}>
                    {t('#portal.usersettings.label.postalarea')} <sup>*</sup>
                  </span>
                  <ValidationWarning
                    text={_get(errors, 'AddressPostalArea')}
                    handleInitErrors={handleInitErrors}
                    Component={
                      <TextField
                        disabled={disabledFields}
                        value={values.Address?.PostalArea || ''}
                        onChange={handleChangeInput}
                        name="Address.PostalArea"
                        classes={inputClasses}
                        InputProps={inputProps}
                      />
                    }
                  />
                </div>
              </div>
              <div className={classes.directionColumn}>
                <span className={classes.label}>
                  {t('#portal.usersettings.label.country')} <sup>*</sup>
                </span>
                <ValidationWarning
                  text={_get(errors, 'AddressCountry')}
                  handleInitErrors={handleInitErrors}
                  Component={
                    <Autocomplete
                      disabled={disabledFields}
                      value={values.Address?.Country || ''}
                      options={countries}
                      onChange={handleChangeCountry}
                      classes={{
                        paper: classes.paper,
                      }}
                      renderInput={(params) => (
                        <TextField
                          name="Address.Country"
                          classes={{
                            root: classes.inputBorder,
                          }}
                          InputProps={{
                            ref: params.InputProps.ref,
                            disableUnderline: true,
                            classes: {
                              input: classes.input,
                            },
                            inputProps: { ...params.inputProps },
                          }}
                        />
                      )}
                    />
                  }
                />
              </div>
              <div className={classes.checkboxColumn}>
                <Checkbox
                  checked={openAlternative}
                  onChange={handleToggleAlternativeView}
                  disabled={upload || disabledFields}
                />
                <span className={classes.checkboxLabel}>
                  {t('#portal.usersettings.label.mailaddressdifferent')}
                </span>
              </div>
              {openAlternative ? renderAlternative() : null}
            </TitledBox>
            <TitledBox
              title={'#portal.usersettings.section.requisites'}
              cls={{
                box: clsx(classes.titledBox, classes.p15, classes.alignStart),
              }}
            >
              <div className={classes.bankGrid}>
                <div className={classes.bankGridRow}>
                  <div className={classes.bankGridItem}>
                    <span className={classes.bankGridItemText}>
                      {t('#portal.usersettings.label.bankaccount')}
                    </span>
                  </div>
                  <div className={classes.bankGridItem}>
                    <span className={classes.bankGridItemText}>
                      {t(
                        '##attribute#uds_bankaccount#uds_isreimbursementaccount'
                      )}
                    </span>
                  </div>
                  <div className={classes.bankGridItem}>
                    <span className={classes.bankGridItemText}>
                      {t('##attribute#uds_bankaccount#uds_iscommissionaccount')}
                    </span>
                  </div>
                </div>
                {values?.BankAccounts.map((BankAccount, idx) => {
                  return (
                    <div
                      className={clsx(classes.bankGridRow, classes.mt20)}
                      key={BankAccount.Id}
                    >
                      <div className={classes.bankGridItem}>
                        <ValidationWarning
                          text={_get(errors, `BankAccount_${idx}_Number`)}
                          handleInitErrors={handleInitErrors}
                          Component={
                            <TextField
                              disabled={disabledFields}
                              name={`BankAccounts.${idx}.Number`}
                              value={BankAccount.Number || ''}
                              onChange={handleChangeInput}
                              placeholder="FI1234567890101112"
                              classes={inputClasses}
                              InputProps={inputProps}
                            />
                          }
                        />
                        <Button
                          className={classes.deleteBtn}
                          onClick={() => removeBankAccount(BankAccount.Id)}
                          disabled={disabledFields}
                        >
                          <DeleteIcon className={classes.deleteIcon} />
                        </Button>
                      </div>
                      <div className={classes.bankGridItem}>
                        <ValidationWarning
                          text={_get(
                            errors,
                            `BankAccount_${idx}_IsReimbursement`
                          )}
                          handleInitErrors={handleInitErrors}
                          Component={
                            <Checkbox
                              disabled={disabledFields}
                              name={`BankAccounts.${idx}.IsReimbursement`}
                              checked={BankAccount.IsReimbursement}
                              onChange={handleToggleCheckbox}
                            />
                          }
                        />
                      </div>
                      <div className={classes.bankGridItem}>
                        <Checkbox
                          disabled={disabledFields}
                          name={`BankAccounts.${idx}.IsCommission`}
                          checked={BankAccount.IsCommission}
                          onChange={handleToggleCheckbox}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
              {values.BankAccounts.length < 3 && values.CanEditUserSettings ? (
                <ButtonShape
                  className={classes.addBankAccountBtn}
                  onClick={addBankAccount}
                  disabled={disabledFields}
                >
                  {t('Create')}
                </ButtonShape>
              ) : null}
              <div className={clsx(classes.directionColumn, classes.mt20)}>
                <span className={classes.label}>
                  {t('##attribute#contact#uds_tax')}
                </span>
                <TextField
                  disabled={disabledFields}
                  type="number"
                  value={values.Tax || 0}
                  onChange={handleChangeTax}
                  classes={inputClasses}
                  InputProps={inputProps}
                />
              </div>
            </TitledBox>
            <TitledBox
              title={'#portal.usersettings.section.сommunications'}
              cls={{
                box: clsx(classes.titledBox, classes.p15, classes.modifiedBox),
              }}
            >
              <span className={clsx(classes.label, classes.radioTitle)}>
                {t('#portal.usersettings.label.materialdelivery')}
              </span>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormLabel component="legend" classes={formLabelClasses}>
                  {t('#portal.usersettings.label.meetinginvitations')}
                </FormLabel>
                <RadioGroup
                  onChange={handleChangeRadio}
                  value={useMailForMeeting}
                  name={'useMailForMeeting'}
                  className={classes.radioGroup}
                >
                  <FormControlLabel
                    classes={formControlLabelClasses}
                    value={true}
                    control={<Radio />}
                    label={t('#portal.usersettings.label.email')}
                    disabled={disabledFields}
                  />
                  <FormControlLabel
                    classes={formControlLabelClasses}
                    value={false}
                    control={<Radio />}
                    label={t('#portal.usersettings.label.post')}
                    disabled={disabledFields}
                  />
                </RadioGroup>
              </FormControl>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormLabel component="legend" classes={formLabelClasses}>
                  {t('#portal.usersettings.label.releases')}
                </FormLabel>
                <RadioGroup
                  onChange={handleChangeRadio}
                  value={useMailForRealise}
                  name="useMailForRealise"
                  className={classes.radioGroup}
                >
                  <FormControlLabel
                    classes={formControlLabelClasses}
                    value={true}
                    control={<Radio />}
                    label={t('#portal.usersettings.label.email')}
                    disabled={disabledFields}
                  />
                  <FormControlLabel
                    classes={formControlLabelClasses}
                    value={false}
                    control={<Radio />}
                    label={t('#portal.usersettings.label.post')}
                    disabled={disabledFields}
                  />
                </RadioGroup>
              </FormControl>
              <FormControl component="fieldset" className={classes.formControl}>
                <FormLabel component="legend" classes={formLabelClasses}>
                  {t('#portal.usersettings.label.marketingpermission')}
                </FormLabel>
                <RadioGroup
                  onChange={handleChangeRadio}
                  name="MarketingPermission"
                  value={values.MarketingPermission}
                  className={classes.radioGroup}
                >
                  <FormControlLabel
                    classes={formControlLabelClasses}
                    value={true}
                    control={<Radio />}
                    label={t('#portal.option.label.yes')}
                    disabled={disabledFields}
                  />
                  <FormControlLabel
                    classes={formControlLabelClasses}
                    value={false}
                    control={<Radio />}
                    label={t('#portal.option.label.no')}
                    disabled={disabledFields}
                  />
                </RadioGroup>
              </FormControl>
            </TitledBox>
            <TitledBox
              title={'Documents'}
              cls={{
                box: classes.p15,
              }}
            >
              <ThemeProvider theme={theme}>
                <FileLoader
                  entityType={EntityType.User}
                  entityId=""
                  readonly={!values.CanEditUserSettings}
                />
              </ThemeProvider>
            </TitledBox>
          </Flex>
        </BorderedBox>
      </form>
    </div>
  );
};

export default memo(PersonInformationDetails);
